/**
 * Página /{slug}/agenda — vista del médico de sus citas.
 *
 * - Si el usuario es médico: por defecto ve SUS citas.
 * - Si es admin/recepción: puede elegir un profesional del policlínico.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Search,
  Stethoscope,
  User,
} from "lucide-react";
import {
  type Cita,
  type CitaCreate,
  cancelarCita,
  updateCita,
  confirmarCita,
  createCita,
  iniciarAtencion,
  listCitas,
} from "../api/citas";
import { listOferta } from "../api/especialidades";
import { listEspecialidades, listProfesionales } from "../api/profesionales";
import { type Paciente, listPacientes } from "../api/pacientes";
import { listSedes } from "../api/tenants";
import {
  type AgendaRegla,
  type SlotLibre,
  DIAS_SEMANA,
  listBloqueos,
  listReglas,
  listSlotsLibres,
} from "../api/agenda";
import {
  CalendarioDisponibilidad,
  diasQueAtiende,
  proximoDiaDisponible,
} from "../components/CalendarioDisponibilidad";
import { ApiError, formatApiError } from "../api/client";
import { EstadoFlujoDeCita } from "../components/atencion/EstadoFlujo";
import { useAuth } from "../auth/AuthProvider";
import { useZonaHoraria, useBase } from "../auth/TenantBrandingProvider";
import { fechaHora, fechaLarga, hora, hoyEnZona } from "../lib/fecha";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Dialog,
  Field,
  Input,
} from "../components/ui";


function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Por qué no hay horarios libres. Distinguir las tres causas evita mandar al
 * usuario a "elegir otra fecha" cuando ninguna fecha va a funcionar.
 */
function motivoSinSlots(reglas: AgendaRegla[], fecha: string): string {
  const activas = reglas.filter((r) => r.activo);
  if (activas.length === 0) {
    return "Este profesional no tiene horarios de atención configurados todavía. Configúralos en su ficha, en «Agenda del profesional».";
  }
  // fecha es YYYY-MM-DD; el mediodía evita que el parseo se corra de día.
  const dia = (new Date(`${fecha}T12:00:00`).getDay() + 6) % 7; // 0=Lunes
  if (!activas.some((r) => r.dia_semana === dia)) {
    const atiende = [...new Set(activas.map((r) => DIAS_SEMANA[r.dia_semana]))].join(", ");
    return `No atiende los ${DIAS_SEMANA[dia].toLowerCase()}. Sus días son: ${atiende}.`;
  }
  return "Ese día ya tiene todos sus horarios ocupados. Prueba con otra fecha.";
}

/** Query param numérico, o null si falta o no es un número. */
function numParam(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function AgendaPage() {
  const base = useBase();
  const tz = useZonaHoraria();
  const [searchParams] = useSearchParams();
  const { me } = useAuth();
  const queryClient = useQueryClient();

  // POR PERMISO, NO POR EL CÓDIGO DEL ROL.
  //
  // Esta pantalla miraba `roles.has("admin_tenant")`, `"recepcion"` y
  // `"medico"`. En un policlínico pequeño la gente cubre varios puestos y los
  // roles se crean a medida: el día que alguien arma un rol "asistente" con
  // permiso de agendar, esta pantalla no le funciona y nada explica por qué.
  //
  // El resto del sistema ya decide por permiso —está escrito así en
  // `tiene_permiso`, precisamente para que los perfiles mixtos y los roles a
  // medida funcionen sin tocar código—; esta se había quedado atrás.
  //
  // Y quién ve "su" agenda no es quien tiene el rol médico: es quien TIENE
  // ficha de profesional. Un odontólogo o una obstetra también pasan consulta,
  // y con el rol como criterio se quedaban sin su propia agenda.
  const puedeVerTodas =
    me?.is_super_admin || (me?.permisos.includes("cita:read") ?? false);
  const esAdminRecepcion = puedeVerTodas;
  const esMedico = me?.profesional_id != null;

  // Estado: fecha del día visible
  // El "hoy" que importa es el del policlínico. Con el navegador en otra zona,
  // ymd(new Date()) podía abrir la agenda en el día equivocado.
  const [fecha, setFecha] = useState<string>(() => hoyEnZona(tz));
  const [nuevaCitaOpen, setNuevaCitaOpen] = useState(false);
  const [reprogramando, setReprogramando] = useState<Cita | null>(null);
  // Estado: especialidad y profesional seleccionados (solo para admin/recepción).
  // Embudo UX: primero especialidad, luego el profesional ya filtrado.
  // Se inicializan desde la URL (?profesional=&especialidad=) para que "Ver
  // agenda" desde la ficha del profesional llegue ya filtrado. Solo se lee al
  // montar: después manda lo que elija el usuario en los selectores.
  const [especialidadId, setEspecialidadId] = useState<number | null>(
    () => numParam(searchParams.get("especialidad")),
  );
  const [profesionalId, setProfesionalId] = useState<number | null>(
    () => numParam(searchParams.get("profesional")),
  );
  // ¿usar "mias"? (médicos por defecto sí; admin no)
  const verMias = esMedico && !profesionalId;

  // Lista de profesionales (solo si admin/recepción)
  const { data: profesionales = [] } = useQuery({
    queryKey: ["profesionales", "all"],
    queryFn: () => listProfesionales(),
    staleTime: 60_000,
    enabled: esAdminRecepcion,
  });

  // Catálogo de especialidades para el primer paso del embudo.
  const { data: especialidades = [] } = useQuery({
    queryKey: ["especialidades"],
    queryFn: listEspecialidades,
    staleTime: 5 * 60_000,
    enabled: esAdminRecepcion,
  });

  // Solo las especialidades que tienen al menos un profesional activo, para no
  // ofrecer especialidades vacías en el selector.
  const especialidadesConProfesional = useMemo(
    () =>
      especialidades.filter((e) =>
        profesionales.some(
          (p) => p.activo && p.especialidades.some((pe) => pe.id === e.id),
        ),
      ),
    [especialidades, profesionales],
  );

  // Profesionales filtrados por la especialidad elegida.
  const profesionalesFiltrados = useMemo(
    () =>
      profesionales.filter(
        (p) =>
          p.activo &&
          (especialidadId == null ||
            p.especialidades.some((e) => e.id === especialidadId)),
      ),
    [profesionales, especialidadId],
  );

  // Al cambiar de especialidad, limpia el profesional si ya no pertenece.
  function cambiarEspecialidad(nuevo: number | null) {
    setEspecialidadId(nuevo);
    if (
      profesionalId != null &&
      nuevo != null &&
      !profesionales.some(
        (p) => p.id === profesionalId && p.especialidades.some((e) => e.id === nuevo),
      )
    ) {
      setProfesionalId(null);
    }
  }

  // Citas
  const {
    data: citas = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["citas", "agenda", fecha, profesionalId, verMias],
    queryFn: () =>
      listCitas({
        fecha,
        profesional_id: profesionalId ?? undefined,
        mias: verMias,
        incluir_canceladas: true,
      }),
  });

  const confirmar = useMutation({
    mutationFn: (id: number) => confirmarCita(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["citas", "agenda"] }),
  });
  // Se confirma antes: cancelar libera la hora y el paciente tiene que volver
  // a pedirla. Un enlace rojo a un clic de distancia no basta.
  const [porCancelar, setPorCancelar] = useState<Cita | null>(null);
  const cancelar = useMutation({
    mutationFn: (id: number) => cancelarCita(id),
    onSuccess: () => {
      setPorCancelar(null);
      queryClient.invalidateQueries({ queryKey: ["citas", "agenda"] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
  });

  const navigate = useNavigate();
  const [atenderError, setAtenderError] = useState<string | null>(null);
  const atender = useMutation({
    mutationFn: (cita: Cita) => iniciarAtencion(cita.id),
    onSuccess: (actualizada) => {
      queryClient.invalidateQueries({ queryKey: ["citas", "agenda"] });
      navigate(
        `${base}/pacientes/${actualizada.paciente_id}/atender?cita=${actualizada.id}`,
      );
    },
    onError: (err) =>
      setAtenderError(formatApiError(err, "No se pudo iniciar la atención.")),
  });

  const ordenadas = useMemo(
    () =>
      [...citas].sort(
        (a, b) =>
          new Date(a.inicio).getTime() - new Date(b.inicio).getTime(),
      ),
    [citas],
  );

  function cambiarDia(delta: number) {
    const d = new Date(fecha);
    d.setDate(d.getDate() + delta);
    setFecha(ymd(d));
  }

  // `fecha` es un día suelto, no un instante: el mediodía evita que el parseo
  // se corra de día al aplicar la zona.
  const fechaLegible = fechaLarga(`${fecha}T12:00:00`, tz);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[color:var(--color-link)]" />
            {esMedico && !esAdminRecepcion ? "Mi agenda" : "Agenda"}
          </h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">{fechaLegible}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => cambiarDia(-1)}
            className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
            title="Día anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />
          <button
            onClick={() => cambiarDia(1)}
            className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
            title="Día siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <Button variant="ghost" onClick={() => setFecha(ymd(new Date()))}>
            Hoy
          </Button>
          {esAdminRecepcion && (
            <Button onClick={() => setNuevaCitaOpen(true)}>
              <Plus className="w-4 h-4" />
              Nueva cita
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={porCancelar !== null}
        onClose={() => setPorCancelar(null)}
        onConfirm={() => porCancelar && cancelar.mutate(porCancelar.id)}
        loading={cancelar.isPending}
        title="Cancelar cita"
        description="La hora queda libre para otro paciente y este tendrá que pedir una nueva. La cita no se borra: queda en el historial como cancelada."
        detalle={
          porCancelar && (
            <>
              <span className="font-medium">{porCancelar.paciente_nombre}</span>
              <br />
              {fechaHora(porCancelar.inicio, tz)} · {porCancelar.especialidad_nombre}
              {porCancelar.pago_estado === "PAGADO" && (
                <span className="mt-1 block text-amber-700">
                  Ya está cobrada: la devolución se resuelve en caja.
                </span>
              )}
            </>
          )
        }
        confirmLabel="Sí, cancelar la cita"
        cancelLabel="No, dejarla"
      />

      {esAdminRecepcion && (
        <>
          <ReprogramarDialog cita={reprogramando} onClose={() => setReprogramando(null)} />
          <NuevaCitaDialog
            open={nuevaCitaOpen}
            onClose={() => setNuevaCitaOpen(false)}
            fechaInicial={fecha}
          />
        </>
      )}

      {esAdminRecepcion && (
        <Card>
          <CardBody>
            <div className="flex flex-wrap items-center gap-3">
              <label className="text-sm font-medium text-slate-700">
                Ver agenda de:
              </label>
              {/* Paso 1 — especialidad */}
              <select
                value={especialidadId ?? ""}
                onChange={(e) =>
                  cambiarEspecialidad(e.target.value ? Number(e.target.value) : null)
                }
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm min-w-[220px]"
              >
                <option value="">— todas las especialidades —</option>
                {especialidadesConProfesional.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                  </option>
                ))}
              </select>
              {/* Paso 2 — profesional (filtrado por la especialidad) */}
              <select
                value={profesionalId ?? ""}
                onChange={(e) =>
                  setProfesionalId(e.target.value ? Number(e.target.value) : null)
                }
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm min-w-[280px]"
              >
                <option value="">
                  {esMedico
                    ? "— Yo —"
                    : especialidadId
                      ? "— seleccionar profesional —"
                      : "— todos / seleccionar profesional —"}
                </option>
                {profesionalesFiltrados.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre_completo} ({p.tipo_colegio} {p.colegiatura})
                  </option>
                ))}
              </select>
              {especialidadId != null && profesionalesFiltrados.length === 0 && (
                <span className="text-xs text-amber-700">
                  No hay profesionales activos en esta especialidad.
                </span>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {error && <Alert>No se pudo cargar la agenda.</Alert>}

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-slate-900">
            {ordenadas.length === 0
              ? "Sin citas para este día"
              : `${ordenadas.length} ${ordenadas.length === 1 ? "cita" : "citas"}`}
          </h2>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-10 text-center text-sm text-slate-400">
              Cargando…
            </div>
          )}
          {!isLoading && ordenadas.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              No hay citas para este día.
              {esMedico && !profesionalId && (
                <p className="text-xs mt-2 text-slate-400">
                  Si recepción ya agendó algo, asegúrate de estar viendo el día
                  correcto.
                </p>
              )}
            </div>
          )}
          {atenderError && (
            <div className="px-6 pt-4">
              <Alert>{atenderError}</Alert>
            </div>
          )}
          {ordenadas.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {ordenadas.map((c) => (
                <CitaAgendaRow
                  key={c.id}
                  c={c}
                  onConfirmar={() => confirmar.mutate(c.id)}
                  onCancelar={() => setPorCancelar(c)}
                  onReprogramar={() => setReprogramando(c)}
                  onAtender={() => {
                    setAtenderError(null);
                    atender.mutate(c);
                  }}
                  esMedico={esMedico}
                />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function CitaAgendaRow({
  c,
  onConfirmar,
  onCancelar,
  onReprogramar,
  onAtender,
  esMedico,
}: {
  c: Cita;
  onConfirmar: () => void;
  onCancelar: () => void;
  onReprogramar: () => void;
  onAtender: () => void;
  esMedico: boolean;
}) {
  const base = useBase();
  const tz = useZonaHoraria();
  const hi = hora(c.inicio, tz);
  const hf = hora(c.fin, tz);
  const { me } = useAuth();
  // Confirmar, cancelar y reprogramar los rechaza el backend sin
  // `paciente:update`. Mostrar el botón igual solo sirve para que alguien lo
  // pulse y reciba un error: si no puede, no se le ofrece.
  const puedeGestionar =
    me?.is_super_admin || (me?.permisos.includes("paciente:update") ?? false);
  const citaViva =
    c.estado === "PROGRAMADA" || c.estado === "CONFIRMADA" || c.estado === "EN_ATENCION";
  const canActuar = citaViva && puedeGestionar;

  return (
    <li className="px-6 py-3 hover:bg-slate-50/50">
      <div className="flex items-start gap-4">
        <div className="text-right shrink-0 w-24">
          <div className="font-mono text-sm font-semibold text-slate-900">
            {hi}
          </div>
          <div className="text-xs text-slate-400">→ {hf}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {/* Un solo estado, el del flujo: "Programada + Pagada" obligaba a
                sumar dos etiquetas para deducir dónde está el paciente. */}
            <EstadoFlujoDeCita cita={c} />
            <Badge>{c.especialidad_nombre}</Badge>
            <span className="text-xs text-slate-500">· {c.sede_nombre}</span>
          </div>
          <Link
            to={`${base}/pacientes/${c.paciente_id}`}
            className="text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium text-sm"
          >
            <User className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
            {c.paciente_nombre}
          </Link>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <Stethoscope className="w-3 h-3" />
            Con: {c.profesional_nombre}
          </div>
          {c.motivo && (
            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
              {c.motivo}
            </p>
          )}
          {c.triaje_id && (
            <p className="text-xs text-amber-700 mt-1">
              <Clock className="w-3 h-3 inline -mt-0.5 mr-0.5" />
              Cita ligada a un triaje del paciente.
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {citaViva && esMedico &&
            (c.pagada ? (
              <button
                type="button"
                onClick={onAtender}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[color:var(--color-link)] hover:bg-[color:var(--color-link)] text-white shadow-sm focus:ring-[color:var(--color-link)] px-4 py-2 text-sm font-medium"
              >
                Atender
              </button>
            ) : (
              <button
                type="button"
                disabled
                title="Pendiente de pago — regístralo en Caja antes de atender"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-200 text-slate-400 px-4 py-2 text-sm font-medium cursor-not-allowed"
              >
                Atender
              </button>
            ))}
          {c.estado === "PROGRAMADA" && puedeGestionar && (
            <button
              onClick={onConfirmar}
              className="text-xs text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium inline-flex items-center gap-1"
            >
              <Check className="w-3 h-3" />
              Confirmar
            </button>
          )}
          {canActuar && (
            <button
              onClick={onCancelar}
              className="text-xs text-slate-500 hover:text-red-600"
            >
              Cancelar
            </button>
          )}
          {canActuar && (
            <button
              onClick={onReprogramar}
              className="text-xs text-slate-500 hover:text-[color:var(--color-link)]"
            >
              Reprogramar
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function nombrePaciente(p: Paciente): string {
  return `${p.nombres} ${p.ap_paterno}${p.ap_materno ? " " + p.ap_materno : ""}`;
}

/**
 * Alta de cita desde la Agenda (recepción). Flujo con sentido:
 * paciente → especialidad → profesional → horario libre real → agendar.
 * La cita hereda el precio (tarifa) de la especialidad, editable aquí.
 */
function NuevaCitaDialog({
  open,
  onClose,
  fechaInicial,
}: {
  open: boolean;
  onClose: () => void;
  fechaInicial: string;
}) {
  const queryClient = useQueryClient();
  const tz = useZonaHoraria();

  const [pacienteSearch, setPacienteSearch] = useState("");
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  // El diálogo tiene su propio embudo (paciente → especialidad → profesional →
  // horario): arranca vacío, no hereda el filtro de la URL de la página.
  const [especialidadId, setEspecialidadId] = useState<number | null>(null);
  const [profesionalId, setProfesionalId] = useState<number | null>(null);
  const [sedeId, setSedeId] = useState<number | null>(null);
  const [servicioId, setServicioId] = useState<number | null>(null);
  const [fecha, setFecha] = useState(fechaInicial);
  const [slot, setSlot] = useState<SlotLibre | null>(null);
  const [monto, setMonto] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPacienteSearch("");
      setPaciente(null);
      setEspecialidadId(null);
      setProfesionalId(null);
      setSedeId(null);
      setFecha(fechaInicial);
      setSlot(null);
      setMonto("");
      setMotivo("");
      setError(null);
    }
  }, [open, fechaInicial]);

  const { data: pacientes = [] } = useQuery({
    queryKey: ["pacientes", pacienteSearch],
    queryFn: () => listPacientes(pacienteSearch, 20),
    enabled: open && paciente === null && pacienteSearch.trim().length >= 2,
  });
  const { data: especialidades = [] } = useQuery({
    queryKey: ["especialidades"],
    queryFn: listEspecialidades,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const { data: profesionales = [] } = useQuery({
    queryKey: ["profesionales", "all"],
    queryFn: () => listProfesionales(),
    staleTime: 60_000,
    enabled: open,
  });
  const { data: sedes = [] } = useQuery({
    queryKey: ["sedes"],
    queryFn: listSedes,
    staleTime: 5 * 60_000,
    enabled: open,
  });

  // Servicios de la especialidad elegida: son los que fijan el precio.
  const { data: oferta = [] } = useQuery({
    queryKey: ["oferta-especialidades"],
    queryFn: listOferta,
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const serviciosDisponibles = useMemo(() => {
    const esp = oferta.find((o) => o.especialidad_id === especialidadId);
    return (esp?.servicios ?? []).filter((s) => s.activo);
  }, [oferta, especialidadId]);

  // Al cambiar de especialidad, preselecciona su consulta: lo habitual es esa,
  // y así quien solo cobra consultas no tiene que elegir nada.
  useEffect(() => {
    if (!open) return;
    const consulta = serviciosDisponibles.find((s) => s.es_consulta);
    setServicioId(consulta?.id ?? serviciosDisponibles[0]?.id ?? null);
  }, [open, especialidadId, serviciosDisponibles]);

  // El monto sigue al servicio salvo que lo hayan tocado a mano.
  const servicioElegido = serviciosDisponibles.find((s) => s.id === servicioId);

  // Días y bloqueos del profesional: con esto el calendario apaga los días
  // imposibles en vez de dejar elegirlos y quejarse después.
  const { data: reglasProf = [] } = useQuery({
    queryKey: ["agenda", "reglas", profesionalId],
    queryFn: () => listReglas(profesionalId as number),
    enabled: open && profesionalId != null,
  });
  const { data: bloqueosProf = [] } = useQuery({
    queryKey: ["agenda", "bloqueos", profesionalId],
    queryFn: () => listBloqueos(profesionalId as number),
    enabled: open && profesionalId != null,
  });

  // Al elegir profesional, saltar al primer día en que atienda. Si no, el
  // formulario se queda clavado en una fecha donde nunca habrá horarios.
  useEffect(() => {
    if (!open || profesionalId == null || reglasProf.length === 0) return;
    const disp = { reglas: reglasProf, bloqueos: bloqueosProf };
    const dias = diasQueAtiende(reglasProf);
    const dowActual = (new Date(`${fecha}T12:00:00`).getDay() + 6) % 7;
    const hoy = hoyEnZona(tz);
    const sirve = dias.has(dowActual) && fecha >= hoy;
    if (sirve) return;
    const proximo = proximoDiaDisponible(fecha < hoy ? hoy : fecha, disp);
    if (proximo) {
      setFecha(proximo);
      setSlot(null);
    }
    // `fecha` fuera de deps a propósito: esto corrige la fecha al cambiar de
    // profesional, no debe re-dispararse cada vez que el usuario elige un día.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, profesionalId, reglasProf, bloqueosProf, tz]);

  useEffect(() => {
    if (open && sedeId == null && sedes.length > 0) setSedeId(sedes[0].id);
  }, [open, sedes, sedeId]);

  const especialidadesConProf = especialidades.filter((e) =>
    profesionales.some(
      (p) => p.activo && p.especialidades.some((pe) => pe.id === e.id),
    ),
  );
  const profesionalesValidos = profesionales.filter(
    (p) =>
      p.activo &&
      especialidadId != null &&
      p.especialidades.some((e) => e.id === especialidadId) &&
      (sedeId == null || p.sedes.some((s) => s.id === sedeId)),
  );

  // Las reglas del profesional: sin ellas no se puede distinguir "no tiene
  // horarios configurados" de "ese día no atiende" o "ya está lleno", y el
  // aviso terminaba culpando a la fecha cuando el problema era otro.
  const { data: reglas = [] } = useQuery({
    queryKey: ["agenda", "reglas", profesionalId],
    queryFn: () => listReglas(profesionalId as number),
    enabled: profesionalId != null,
  });

  const { data: slots = [], isLoading: cargandoSlots } = useQuery({
    queryKey: ["slots", profesionalId, fecha, sedeId],
    queryFn: () => listSlotsLibres(profesionalId!, fecha, sedeId),
    enabled: open && profesionalId != null && !!fecha,
  });

  function elegirEspecialidad(id: number | null) {
    setEspecialidadId(id);
    setProfesionalId(null);
    setSlot(null);
    const e = especialidades.find((x) => x.id === id);
    setMonto(e?.precio != null ? String(e.precio) : "");
  }

  const mutation = useMutation({
    mutationFn: (p: CitaCreate) => createCita(p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["citas"] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError("Ese horario ya fue tomado. Elige otro.");
      } else {
        setError(formatApiError(err, "No se pudo agendar la cita."));
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!paciente || especialidadId == null || profesionalId == null || slot == null) {
      setError("Elige paciente, especialidad, profesional y un horario.");
      return;
    }
    mutation.mutate({
      paciente_id: paciente.id,
      profesional_id: profesionalId,
      especialidad_id: especialidadId,
      sede_id: slot.sede_id,
      inicio: slot.inicio,
      fin: slot.fin,
      motivo: motivo || undefined,
      monto: monto === "" ? undefined : monto,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nueva cita"
      description="Agenda una cita: paciente, especialidad, profesional y un horario libre."
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {/* Paso 1 — Paciente */}
        <Field label="Paciente *" htmlFor="nc_pac">
          {paciente ? (
            <div className="flex items-center justify-between rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm">
              <span>
                <span className="font-medium text-slate-800">{nombrePaciente(paciente)}</span>
                <span className="ml-2 font-mono text-xs text-slate-500">
                  {paciente.tipo_documento} {paciente.num_documento}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setPaciente(null)}
                className="text-xs text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <div className="relative">
              <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  id="nc_pac"
                  value={pacienteSearch}
                  onChange={(e) => setPacienteSearch(e.target.value)}
                  placeholder="Buscar por nombre o DNI…"
                  className="flex-1 bg-transparent border-0 outline-none text-sm"
                  autoComplete="off"
                />
              </div>
              {pacienteSearch.trim().length >= 2 && pacientes.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {pacientes.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setPaciente(p);
                          setPacienteSearch("");
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                      >
                        {nombrePaciente(p)}
                        <span className="ml-2 font-mono text-xs text-slate-500">
                          {p.tipo_documento} {p.num_documento}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {pacienteSearch.trim().length >= 2 && pacientes.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">Sin coincidencias.</p>
              )}
            </div>
          )}
        </Field>

        {/* Paso 2 — Especialidad → Profesional → Sede */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Especialidad *" htmlFor="nc_esp">
            <select
              id="nc_esp"
              value={especialidadId ?? ""}
              onChange={(e) => elegirEspecialidad(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              required
            >
              <option value="">— seleccionar —</option>
              {especialidadesConProf.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Profesional *" htmlFor="nc_prof">
            <select
              id="nc_prof"
              value={profesionalId ?? ""}
              onChange={(e) => {
                setProfesionalId(e.target.value ? Number(e.target.value) : null);
                setSlot(null);
              }}
              disabled={especialidadId == null}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              required
            >
              <option value="">
                {especialidadId == null ? "— elige especialidad —" : "— seleccionar —"}
              </option>
              {profesionalesValidos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre_completo}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sede *" htmlFor="nc_sede">
            <select
              id="nc_sede"
              value={sedeId ?? ""}
              onChange={(e) => {
                setSedeId(e.target.value ? Number(e.target.value) : null);
                setSlot(null);
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              required
            >
              <option value="">— seleccionar —</option>
              {sedes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Paso 3 — Fecha + horarios libres */}
        <Field
          label="Fecha *"
          htmlFor="nc_fecha"
          hint={
            profesionalId == null
              ? "Elige un profesional para ver sus días de atención."
              : undefined
          }
        >
          <CalendarioDisponibilidad
            value={fecha}
            deshabilitado={profesionalId == null}
            disponibilidad={{ reglas: reglasProf, bloqueos: bloqueosProf, hoy: hoyEnZona(tz) }}
            onChange={(f) => {
              setFecha(f);
              setSlot(null);
            }}
          />
        </Field>

        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Horarios libres</p>
          {profesionalId == null && (
            <p className="text-xs text-slate-500">Elige profesional para ver sus horarios.</p>
          )}
          {profesionalId != null && cargandoSlots && (
            <p className="text-xs text-slate-500">Cargando agenda…</p>
          )}
          {profesionalId != null && !cargandoSlots && slots.length === 0 && (
            <p className="text-xs text-amber-700">{motivoSinSlots(reglas, fecha)}</p>
          )}
          {slots.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-40 overflow-y-auto">
              {slots.map((s) => {
                const elegido = slot?.inicio === s.inicio;
                const hi = hora(s.inicio, tz);
                return (
                  <button
                    key={s.inicio}
                    type="button"
                    onClick={() => setSlot(s)}
                    className={`px-2 py-1.5 rounded border text-xs font-mono ${
                      elegido
                        ? "bg-[color:var(--color-link)] text-white border-[color:var(--color-link)]"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
                    }`}
                  >
                    {hi}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Paso 4 — Servicio + monto + motivo */}
        {serviciosDisponibles.length > 1 && (
          <Field
            label="Servicio *"
            htmlFor="nc_servicio"
            hint="Qué se le va a hacer. Define el precio."
          >
            <select
              id="nc_servicio"
              value={servicioId ?? ""}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                setServicioId(id);
                const s = serviciosDisponibles.find((x) => x.id === id);
                if (s) setMonto(String(Number(s.precio)));
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              {serviciosDisponibles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} — S/ {Number(s.precio).toFixed(2)}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Monto (S/)"
            htmlFor="nc_monto"
            hint={
              servicioElegido
                ? `Precio de ${servicioElegido.nombre}; puedes ajustarlo.`
                : "Se hereda de la tarifa; puedes ajustarlo."
            }
          >
            <Input
              id="nc_monto"
              type="number"
              min="0"
              step="0.01"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0.00"
            />
          </Field>
        </div>

        {/* El motivo va a fila completa y en varias líneas: es lo primero que
            lee el profesional antes de atender, y quien registra la cita
            necesita sitio para escribir algo útil, no media línea. */}
        <Field
          label="Motivo / nota"
          htmlFor="nc_mot"
          hint="Opcional, pero ayuda: el profesional lo ve antes de la consulta."
        >
          <textarea
            id="nc_mot"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={4000}
            rows={3}
            placeholder="Ej. Control de ansiedad, seguimiento de tratamiento. Refiere insomnio hace 2 semanas."
            className="w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)]"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending} disabled={slot == null}>
            Agendar cita
          </Button>
        </div>
      </form>
    </Dialog>
  );
}


/**
 * Mover una cita a otro horario del MISMO profesional.
 *
 * Antes no existía: para cambiar la hora había que cancelar y crear otra, con
 * lo que el paciente quedaba con una cita "CANCELADA" en su historial como si
 * hubiera faltado, y se perdía el motivo y el monto ya acordado.
 *
 * El backend valida el solape y se niega a mover citas ya completadas o
 * canceladas, así que la lista de horarios muestra solo huecos reales.
 */
function ReprogramarDialog({
  cita,
  onClose,
}: {
  cita: Cita | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const tz = useZonaHoraria();
  const [fecha, setFecha] = useState("");
  const [slot, setSlot] = useState<SlotLibre | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iniciado, setIniciado] = useState<number | null>(null);

  const abierto = cita != null;
  const profId = cita?.profesional_id ?? null;

  const { data: reglas = [] } = useQuery({
    queryKey: ["agenda", "reglas", profId],
    queryFn: () => listReglas(profId as number),
    enabled: abierto && profId != null,
  });
  const { data: bloqueos = [] } = useQuery({
    queryKey: ["agenda", "bloqueos", profId],
    queryFn: () => listBloqueos(profId as number),
    enabled: abierto && profId != null,
  });
  const { data: slots = [], isLoading: cargandoSlots } = useQuery({
    queryKey: ["slots", profId, fecha],
    queryFn: () => listSlotsLibres(profId as number, fecha),
    enabled: abierto && profId != null && !!fecha,
  });

  // Arranca en el día de la cita si el profesional atiende ese día; si no, en
  // el próximo disponible.
  if (abierto && iniciado !== cita.id) {
    setIniciado(cita.id);
    const hoy = hoyEnZona(tz);
    const dia = cita.inicio.slice(0, 10);
    setFecha(dia >= hoy ? dia : hoy);
    setSlot(null);
    setError(null);
  }
  if (!abierto && iniciado !== null) setIniciado(null);

  const mover = useMutation({
    mutationFn: () =>
      updateCita(cita!.id, { inicio: slot!.inicio, fin: slot!.fin }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["citas"] });
      queryClient.invalidateQueries({ queryKey: ["slots"] });
      onClose();
    },
    onError: (e) => setError(formatApiError(e, "No se pudo reprogramar la cita.")),
  });

  if (!cita) return null;

  return (
    <Dialog
      open={abierto}
      onClose={onClose}
      title="Reprogramar cita"
      description="Se mueve la misma cita: conserva paciente, especialidad, motivo y monto."
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <div className="font-medium text-slate-800">{cita.paciente_nombre}</div>
          <div className="text-xs text-slate-500">
            {cita.especialidad_nombre} · {cita.profesional_nombre}
          </div>
          <div className="mt-1 text-xs text-slate-600">
            Ahora: <span className="font-mono">{fechaHora(cita.inicio, tz)}</span>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Nueva fecha</p>
          <CalendarioDisponibilidad
            value={fecha || hoyEnZona(tz)}
            disponibilidad={{ reglas, bloqueos, hoy: hoyEnZona(tz) }}
            onChange={(f) => {
              setFecha(f);
              setSlot(null);
            }}
          />
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Nuevo horario</p>
          {cargandoSlots ? (
            <p className="text-xs text-slate-500">Cargando…</p>
          ) : slots.length === 0 ? (
            <p className="text-xs text-amber-700">{motivoSinSlots(reglas, fecha)}</p>
          ) : (
            <div className="grid max-h-40 grid-cols-4 gap-2 overflow-y-auto sm:grid-cols-6">
              {slots.map((s) => {
                const elegido = slot?.inicio === s.inicio;
                return (
                  <button
                    key={s.inicio}
                    type="button"
                    onClick={() => setSlot(s)}
                    className={`rounded-lg border px-2 py-1.5 font-mono text-xs transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                      elegido
                        ? "border-[color:var(--color-link)] bg-[color:var(--color-link)] text-white"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {hora(s.inicio, tz)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => mover.mutate()} disabled={slot == null || mover.isPending}>
            Mover cita
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
